import React from 'react';
import ProductCartCounter from '../../components/ProductCartCounter';

function productTitle(product, idx) {
  return product?.product_name || product?.name || `Product ${idx + 1}`;
}

function matchPercent(product, idx = 0) {
  const raw = product?.match_score ?? product?.match_percent ?? product?.match ?? product?.similarity;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (!Number.isNaN(n)) {
      if (n > 0 && n <= 1) return Math.round(n * 100);
      return Math.round(Math.max(0, Math.min(100, n)));
    }
  }
  const why = Array.isArray(product?.suggested_for_conditions) ? product.suggested_for_conditions.length : 0;
  if (why > 0) return Math.min(97, 80 + why * 3);
  return Math.max(75, 94 - (idx % 6) * 3);
}

export default function HairInsightReportProductsSection({
  products = { morning: [], evening: [] },
  loading = false,
  error = '',
  visible = false,
  productCartItems = {},
  onProductCartChange,
  onRetry,
  confirmBusy = false,
  confirmNotice = '',
  onConfirmProducts,
}) {
  if (!visible) return null;

  const morning = Array.isArray(products?.morning) ? products.morning : [];
  const evening = Array.isArray(products?.evening) ? products.evening : [];
  const list = [...morning, ...evening];
  const cartTotal = Object.values(productCartItems || {}).reduce(
    (sum, row) => sum + (Number(row?.quantity) || 0),
    0,
  );

  const handleQty = (product, productId, quantity) => {
    if (typeof onProductCartChange !== 'function') return;
    onProductCartChange((prev) => {
      const next = { ...(prev || {}) };
      if (!quantity) {
        delete next[productId];
      } else {
        next[productId] = { product, quantity };
      }
      return next;
    });
  };

  return (
    <div className="skin-report-section skin-report-reco-section hi-report-products-section" id="section-hair-products">
      <h2 className="skin-report-section-heading">
        <span className="skin-report-section-num" aria-hidden="true">6</span>
        <span className="skin-report-section-icon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M10 7h4" /></svg>
        </span>
        AI Recommended Products for You
      </h2>
      <div className="skin-report-reco skin-report-products-reco">
        {loading && list.length === 0 ? (
          <div className="skin-routine-summary-loading">
            <div className="loading-spinner" />
            <p className="skin-routine-summary-loading-text">Generating recommended products...</p>
          </div>
        ) : error && list.length === 0 ? (
          <div className="skin-routine-summary-error-wrap">
            <p className="skin-routine-summary-text" role="status">{error}</p>
            {onRetry ? (
              <button type="button" className="skin-action-button skin-action-button-secondary" onClick={onRetry}>
                Retry products
              </button>
            ) : null}
          </div>
        ) : list.length > 0 ? (
          <>
            <div className="skin-report-products-row">
              {list.map((product, idx) => {
                const title = productTitle(product, idx);
                const productId = product?.product_id || product?.id || `hair_product_${title}_${idx}`;
                const cartQuantity = productCartItems?.[productId]?.quantity || 0;
                const imageUrl = product?.image_url || product?.imageUrl || '';
                const why = product?.suggested_for_wording
                  || (Array.isArray(product?.suggested_for_conditions) && product.suggested_for_conditions.length
                    ? `Recommended for ${product.suggested_for_conditions.join(', ')}`
                    : '');
                return (
                  <article key={`${productId}-${idx}`} className="skin-report-product-card">
                    <div className="skin-report-product-media">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={title}
                          className="skin-report-product-image"
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      ) : (
                        <div className="skin-report-product-image-fallback" aria-hidden="true" />
                      )}
                    </div>
                    <div className="skin-report-product-body">
                      <h3 className="skin-report-product-name">{title}</h3>
                      <p className="skin-report-product-match">{matchPercent(product, idx)}% Match</p>
                      {why ? <p className="skin-report-products-note" style={{ margin: 0 }}>{why}</p> : null}
                      <div className="skin-report-product-actions">
                        <ProductCartCounter
                          key={`hair-pc-${productId}`}
                          product={product}
                          onAddToCart={(p, quantity) => handleQty(p, productId, quantity)}
                          initialQuantity={cartQuantity}
                          quantityLabel="Qty"
                          addLabel="Add to Cart"
                        />
                        <span className="skin-report-product-view skin-report-product-view--muted">
                          Front desk
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            <p className="skin-report-products-note">
              Recommended from your hair analysis. Confirm selections for the front desk.
            </p>
            {cartTotal > 0 && typeof onConfirmProducts === 'function' ? (
              <div className="hi-report-treatment-confirm" data-pdf-exclude="true">
                <button
                  type="button"
                  className="skin-action-button skin-routine-continue-treatments-button"
                  disabled={confirmBusy}
                  onClick={onConfirmProducts}
                >
                  {confirmBusy ? 'CONFIRMING…' : `CONFIRM PRODUCTS (${cartTotal})`}
                </button>
                {confirmNotice ? (
                  <p className="skin-routine-summary-text" role="status">{confirmNotice}</p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <div className="skin-report-treatments-empty">
            <p className="skin-routine-summary-text">No product recommendations available yet.</p>
            {onRetry ? (
              <button type="button" className="skin-report-treatments-retry" onClick={onRetry} disabled={loading}>
                Retry products
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
